/**
 * UPMI — bespoke Contact page (handoff Page 5): teal hero, 4 info cards, a
 * message form with an inline success state (posts to /storefront/contact),
 * an embedded Google Map, and online-store + social links.
 */
import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, Clock, Mail, MapPin, Phone } from 'lucide-react';
import { submitContactInquiry } from '../../../lib/api';
import { UpmiShell } from './shell';
import { INK, LINE, MEDIA, MUTED, O, OGRAD, OL, SOCIAL } from './tokens';

const INFO: { Icon: typeof MapPin; title: string; lines: string[] }[] = [
  {
    Icon: MapPin,
    title: 'Office Address',
    lines: [
      'Unit 201, 2nd Floor, J&S Building',
      '104 Kalayaan Avenue, Diliman',
      'Quezon City, Philippines 1101',
    ],
  },
  {
    Icon: Phone,
    title: 'Contact Number',
    lines: ['Landline 02-8461-3237', 'Mobile 0919 095 4930', '0956 033 9475'],
  },
  { Icon: Clock, title: 'Office Hours', lines: ['Monday – Saturday', '9:00 AM – 6:00 PM'] },
  { Icon: Mail, title: 'Email Address', lines: ['ultraproactive2014@gmail.com'] },
];

const MAP_SRC = `https://maps.google.com/maps?q=${encodeURIComponent('J&S Building, 104 Kalayaan Avenue, Diliman, Quezon City')}&z=16&output=embed`;
const inputStyle = {
  width: '100%',
  height: 44,
  border: `1px solid #d8d8d8`,
  borderRadius: 4,
  padding: '0 12px',
  fontSize: 14,
  outline: 'none',
} as const;

export function UpmiContact(): JSX.Element {
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const set = (k: keyof typeof form) => (e: { target: { value: string } }) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    setBusy(true);
    try {
      await submitContactInquiry(form);
      setSent(true);
    } catch {
      setErr('Sorry, something went wrong. Please try again or email us directly.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <UpmiShell active="contact">
      <div style={{ maxWidth: 1180, margin: '0 auto', padding: '20px 16px 64px' }}>
        <div style={{ fontSize: 12.5, color: MUTED, marginBottom: 18 }}>
          <Link to="/" style={{ color: MUTED, textDecoration: 'none' }}>
            Home
          </Link>
          <span style={{ margin: '0 8px', color: '#ccc' }}>›</span>
          <span style={{ color: INK }}>Contact Us</span>
        </div>

        {/* Hero */}
        <section
          style={{
            background: OGRAD,
            borderRadius: 12,
            padding: '48px 24px',
            textAlign: 'center',
            color: '#fff',
          }}
        >
          <h1 style={{ fontSize: 'clamp(26px,3.4vw,38px)', fontWeight: 800, margin: '0 0 10px' }}>
            We are happy to serve you!
          </h1>
          <p style={{ fontSize: 15, opacity: 0.95, margin: 0 }}>
            Questions about products, membership, or your order? Reach out — we&apos;d love to help.
          </p>
        </section>

        {/* Info cards */}
        <section
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit,minmax(240px,1fr))',
            gap: 16,
            marginTop: 28,
          }}
        >
          {INFO.map(({ Icon, title, lines }) => (
            <div
              key={title}
              style={{
                background: '#fff',
                border: `1px solid ${LINE}`,
                borderRadius: 12,
                padding: 22,
              }}
            >
              <span
                style={{
                  width: 46,
                  height: 46,
                  borderRadius: 10,
                  background: OL,
                  color: O,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: 14,
                }}
              >
                <Icon size={22} />
              </span>
              <h3 style={{ fontSize: 15, fontWeight: 800, margin: '0 0 8px', color: INK }}>
                {title}
              </h3>
              {lines.map((l) => (
                <div key={l} style={{ fontSize: 13.5, color: '#555', lineHeight: 1.55 }}>
                  {l}
                </div>
              ))}
            </div>
          ))}
        </section>

        {/* Form + Map */}
        <section
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit,minmax(320px,1fr))',
            gap: 20,
            marginTop: 24,
            alignItems: 'stretch',
          }}
        >
          <div
            style={{
              background: '#fff',
              border: `1px solid ${LINE}`,
              borderRadius: 12,
              padding: 26,
            }}
          >
            {sent ? (
              <div style={{ textAlign: 'center', padding: '40px 10px' }}>
                <CheckCircle2 size={56} color="#0a8a4f" />
                <h2 style={{ fontSize: 22, fontWeight: 800, margin: '16px 0 8px', color: INK }}>
                  Message sent!
                </h2>
                <p style={{ color: MUTED, fontSize: 14.5, margin: 0 }}>
                  Thanks for reaching out — our team will get back to you shortly.
                </p>
              </div>
            ) : (
              <>
                <h2 style={{ fontSize: 22, fontWeight: 800, margin: '0 0 6px', color: INK }}>
                  Send us a message
                </h2>
                <p style={{ color: MUTED, fontSize: 14, margin: '0 0 20px' }}>
                  Fill in the form and we&apos;ll be in touch.
                </p>
                <form
                  onSubmit={onSubmit}
                  style={{ display: 'flex', flexDirection: 'column', gap: 14 }}
                >
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit,minmax(140px,1fr))',
                      gap: 14,
                    }}
                  >
                    <input
                      required
                      placeholder="Full name"
                      value={form.name}
                      onChange={set('name')}
                      style={inputStyle}
                    />
                    <input
                      required
                      type="email"
                      placeholder="Email"
                      value={form.email}
                      onChange={set('email')}
                      style={inputStyle}
                    />
                  </div>
                  <input
                    required
                    placeholder="Subject"
                    value={form.subject}
                    onChange={set('subject')}
                    style={inputStyle}
                  />
                  <textarea
                    required
                    placeholder="Message"
                    value={form.message}
                    onChange={set('message')}
                    rows={5}
                    style={{
                      ...inputStyle,
                      height: 'auto',
                      padding: 12,
                      resize: 'vertical',
                      fontFamily: 'inherit',
                    }}
                  />
                  {err && <div style={{ color: '#c0392b', fontSize: 13 }}>{err}</div>}
                  <button
                    type="submit"
                    disabled={busy}
                    style={{
                      background: O,
                      color: '#fff',
                      fontWeight: 700,
                      fontSize: 14,
                      padding: '13px 16px',
                      borderRadius: 6,
                      border: 0,
                      cursor: busy ? 'default' : 'pointer',
                      opacity: busy ? 0.7 : 1,
                    }}
                  >
                    {busy ? 'Sending…' : 'Send Message'}
                  </button>
                </form>
              </>
            )}
          </div>
          <div
            style={{
              background: '#fff',
              border: `1px solid ${LINE}`,
              borderRadius: 12,
              padding: 26,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <h2 style={{ fontSize: 22, fontWeight: 800, margin: '0 0 14px', color: INK }}>
              Our map location
            </h2>
            <div
              style={{
                flex: 1,
                minHeight: 420,
                borderRadius: 10,
                overflow: 'hidden',
                border: `1px solid ${LINE}`,
              }}
            >
              <iframe
                title="Ultra Proactive office map"
                src={MAP_SRC}
                style={{ width: '100%', height: '100%', minHeight: 420, border: 0 }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>
        </section>

        {/* Stores + Social */}
        <section
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit,minmax(280px,1fr))',
            gap: 20,
            marginTop: 24,
          }}
        >
          <div
            style={{
              background: '#fff',
              border: `1px solid ${LINE}`,
              borderRadius: 12,
              padding: 26,
            }}
          >
            <h3 style={{ fontSize: 16, fontWeight: 800, margin: '0 0 16px', color: INK }}>
              Visit our official Online Stores
            </h3>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <a
                href={SOCIAL.shopee}
                target="_blank"
                rel="noreferrer"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                  border: '1.5px solid #ee4d2d',
                  color: '#ee4d2d',
                  fontWeight: 700,
                  fontSize: 14,
                  padding: '10px 18px',
                  borderRadius: 8,
                  textDecoration: 'none',
                }}
              >
                <img src={`${MEDIA}/shopee.png`} alt="" style={{ height: 18, width: 'auto' }} />{' '}
                Shopee
              </a>
              <a
                href={SOCIAL.lazada}
                target="_blank"
                rel="noreferrer"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                  border: '1.5px solid #0f146d',
                  color: '#0f146d',
                  fontWeight: 700,
                  fontSize: 14,
                  padding: '10px 18px',
                  borderRadius: 8,
                  textDecoration: 'none',
                }}
              >
                Lazada
              </a>
            </div>
          </div>
          <div
            style={{
              background: '#fff',
              border: `1px solid ${LINE}`,
              borderRadius: 12,
              padding: 26,
            }}
          >
            <h3 style={{ fontSize: 16, fontWeight: 800, margin: '0 0 16px', color: INK }}>
              Follow PROMAG300
            </h3>
            <div style={{ display: 'flex', gap: 14 }}>
              {(
                [
                  ['facebook', `${MEDIA}/icons/facebook.svg`],
                  ['instagram', `${MEDIA}/icons/instagram.svg`],
                  ['tiktok', `${MEDIA}/icons/tiktok.svg`],
                ] as [keyof typeof SOCIAL, string][]
              ).map(([key, icon]) => (
                <a
                  key={key}
                  href={SOCIAL[key]}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={key}
                  style={{
                    width: 48,
                    height: 48,
                    borderRadius: '50%',
                    background: OL,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <img src={icon} alt="" style={{ width: 22, height: 22 }} />
                </a>
              ))}
            </div>
          </div>
        </section>
      </div>
    </UpmiShell>
  );
}
